// RoutesPanel — list, add, edit routes. Pickup order is view-only here —
// each stop is auto-derived from a student's guardian's pinned location
// (see Students / Guardians tabs), not created manually.
import { useEffect, useState } from 'react';
import { getRoutes, createRoute, updateRoute, getVans, getRouteStops } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY_ROUTE = { name: '', van_id: '' };

export default function RoutesPanel() {
  const [routes,  setRoutes]  = useState([]);
  const [vans,    setVans]    = useState([]);
  const [form,    setForm]    = useState(EMPTY_ROUTE);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [stops,         setStops]         = useState([]);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [r, v] = await Promise.all([getRoutes(), getVans()]);
      setRoutes(r); setVans(v);
    } catch { setMsg('Failed to load'); }
  }

  function startEdit(route) {
    setEditing(route.id);
    setForm({ name: route.name, van_id: route.van_id || '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY_ROUTE); setMsg(''); }

  async function saveRoute(e) {
    e.preventDefault();
    const payload = { ...form, van_id: form.van_id || null };
    try {
      editing ? await updateRoute(editing, payload) : await createRoute(payload);
      setMsg(editing ? 'Route updated ✅' : 'Route added ✅');
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function viewStops(route) {
    setSelectedRoute(route);
    const data = await getRouteStops(route.id);
    setStops(data.sort((a, b) => a.stop_order - b.stop_order));
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Routes</h2>

      {/* Route form */}
      <Card className="p-5 mb-6 max-w-[400px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Route' : 'Add Route'}</h3>
        <form onSubmit={saveRoute} className="flex flex-col gap-3">
          <label className={labelCls}>Route Name
            <input className={inputCls} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </label>
          <label className={labelCls}>Assign Van
            <select className={inputCls} value={form.van_id} onChange={e => setForm({...form, van_id: e.target.value})}>
              <option value="">— None —</option>
              {vans.map(v => <option key={v.id} value={v.id}>{v.number_plate}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      {/* Routes table */}
      <Card>
        <CardHeader title={`Routes (${routes.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Van</th><th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {routes.map(r => (
                <tr key={r.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{r.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{r.name}</td>
                  <td className="px-4 py-3">{r.number_plate || '—'}</td>
                  <td className="px-4 py-3">{r.driver_name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(r)}>Edit</Button>
                      <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => viewStops(r)}>Pickup Order</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Read-only pickup order view */}
      {selectedRoute && (
        <Card className="p-5 mt-6">
          <h3 className="font-display font-bold text-[15px] text-ink m-0 mb-1">Pickup Order — {selectedRoute.name}</h3>
          <p className="text-ink-soft text-xs mb-3">
            Each pickup point comes from a student's guardian's pinned location (set in Students / Guardians tabs).
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                  <th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Lat</th><th className="px-4 py-2.5">Lng</th>
                </tr>
              </thead>
              <tbody>
                {stops.map(s => (
                  <tr key={s.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                    <td className="px-4 py-3 tabular-nums">{s.stop_order}</td>
                    <td className="px-4 py-3">{s.name}</td>
                    <td className="px-4 py-3 tabular-nums">{s.latitude}</td>
                    <td className="px-4 py-3 tabular-nums">{s.longitude}</td>
                  </tr>
                ))}
                {stops.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-ink-soft">
                    No pickup points yet — add students on this route with a guardian location set
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Button variant="secondary" onClick={() => setSelectedRoute(null)}>Close</Button>
        </Card>
      )}
    </div>
  );
}
